import type { ChatType } from '@form-factory/schema';
import { THEME_PRESETS, AgentDefaults } from '@form-factory/shared';
import { useLingui } from '@lingui/react/macro';
import { Button, Label } from '@utima/ui';
import { useFormApi } from 'informed';
import { Moon, Sun } from 'lucide-react';
import { memo } from 'react';

import { SimpleTooltip } from '@/components';
import { Card } from '@/components/card/Card';
import type { ScopedLazyFileUploadAdapter } from '@/components/fileUpload/adapters/lazyFileUploadAdapter';
import { SingleFileUpload } from '@/components/fileUpload/SingleFileUpload';
import { SchemaFields } from '@/components/form/SchemaFields';
import { useFieldsSchema } from '@/components/form/useFieldsSchema';

export type ChatThemeFieldsProps = {
  data?: ChatType;
  avatarUploadAdapter: ScopedLazyFileUploadAdapter;
  logoUploadAdapter: ScopedLazyFileUploadAdapter;
  bubbleUploadAdapter: ScopedLazyFileUploadAdapter;
};

export const ChatThemeFields = memo(function ChatThemeFields({
  avatarUploadAdapter,
  bubbleUploadAdapter,
  logoUploadAdapter,
}: ChatThemeFieldsProps) {
  const { t } = useLingui();
  const { setValue } = useFormApi();
  const { schema } = useFieldsSchema(
    [
      {
        name: 'themeConfig.preset',
        uiControl: 'hidden',
        defaultValue: AgentDefaults.defaultThemePreset,
      },
      {
        name: 'themeConfig.primary',
        label: t`Primary background`,
        showOptional: false,
        uiProps: {
          type: 'color',
        },
        defaultValue: AgentDefaults.defaultTheme.primary,
      },
      {
        name: 'themeConfig.primaryFg',
        label: t`Primary foreground`,
        showOptional: false,
        uiProps: {
          type: 'color',
        },
        defaultValue: AgentDefaults.defaultTheme.primaryFg,
      },
    ],
    [t],
  );

  const handleThemeChange = (selectedValue: string) => {
    if (selectedValue in THEME_PRESETS) {
      // Get the preset colors
      const preset = THEME_PRESETS[selectedValue as keyof typeof THEME_PRESETS];

      setValue('themeConfig.primary', preset.primary);
      setValue('themeConfig.primaryFg', preset.primaryFg);
      setValue('themeConfig.preset', selectedValue);
    }
  };

  return (
    <Card title={t`Theme config`}>
      <div className='flex flex-col gap-y-2'>
        <Label>{t`Style`}</Label>
        <div className='flex gap-x-2'>
          <SimpleTooltip delayDuration={0} title={t`Dark`}>
            <Button
              data-cta='admin-chat-theme-dark-button'
              variant='ghost'
              outline
              onClick={() => handleThemeChange('dark')}
            >
              <Moon />
            </Button>
          </SimpleTooltip>
          <SimpleTooltip delayDuration={0} title={t`Light`}>
            <Button
              data-cta='admin-chat-theme-light-button'
              variant='ghost'
              outline
              onClick={() => handleThemeChange('light')}
            >
              <Sun />
            </Button>
          </SimpleTooltip>
        </div>
      </div>
      <SchemaFields schema={schema} />
      <SingleFileUpload
        name='chatbotConfig.systemAvatar'
        label={t`System avatar`}
        uploadAdapter={avatarUploadAdapter}
      />
      <SingleFileUpload
        name='themeConfig.logo'
        label={t`Header logo`}
        uploadAdapter={logoUploadAdapter}
      />
      <SingleFileUpload
        name='themeConfig.logoBubble'
        label={t`Logo bubble`}
        uploadAdapter={bubbleUploadAdapter}
      />
    </Card>
  );
});
